import { useCallback } from "react";
import { TRPCClientError } from "@trpc/client";

import { addBreadcrumb, captureError } from "../utils/sentry";

interface ErrorHandlerOptions {
  fallbackMessage?: string;
  showToast?: boolean;
  captureToSentry?: boolean;
  onError?: (error: Error) => void;
}

export function useErrorHandler(options: ErrorHandlerOptions = {}) {
  const {
    fallbackMessage = "Something went wrong. Please try again.",
    showToast = true,
    captureToSentry = true,
    onError,
  } = options;

  const handleError = useCallback(
    (error: unknown) => {
      let errorMessage = fallbackMessage;
      let shouldShowToast = showToast;
      let errorCode: string | undefined;
      let errorType = "unknown";

      // Handle TRPC errors specifically
      if (error instanceof TRPCClientError) {
        errorType = "trpc";
        errorCode = error.data?.code;

        switch (error.data?.code) {
          case "UNAUTHORIZED":
            errorMessage = "You need to be logged in to access this feature.";
            break;
          case "FORBIDDEN":
            errorMessage = "You do not have permission to perform this action.";
            break;
          case "NOT_FOUND":
            errorMessage = "The requested resource was not found.";
            break;
          case "TIMEOUT":
            errorMessage =
              "Request timed out. Please check your connection and try again.";
            break;
          case "INTERNAL_SERVER_ERROR":
            errorMessage =
              "Our servers are experiencing issues. Please try again later.";
            break;
          case "TOO_MANY_REQUESTS":
            errorMessage =
              "Too many requests. Please wait a moment before trying again.";
            break;
          default:
            errorMessage = error.message || fallbackMessage;
        }
      }
      // Handle network errors
      else if (error instanceof Error) {
        errorType = "javascript";

        if (error.message.includes("fetch")) {
          errorMessage =
            "Network error. Please check your connection and try again.";
          errorType = "network";
        } else if (error.message.includes("timeout")) {
          errorMessage = "Request timed out. Please try again.";
          errorType = "timeout";
        } else {
          errorMessage = error.message || fallbackMessage;
        }
      }

      // Add breadcrumb for context
      addBreadcrumb("Error handled by useErrorHandler", "error", {
        errorType,
        errorCode,
        errorMessage,
        originalError: error instanceof Error ? error.message : String(error),
      });

      // Capture to Sentry if enabled
      if (captureToSentry) {
        const errorToCapture =
          error instanceof Error ? error : new Error(String(error));

        captureError(errorToCapture, {
          errorHandler: {
            type: errorType,
            code: errorCode,
            userMessage: errorMessage,
            fallbackMessage,
          },
          location: window.location.href,
          timestamp: new Date().toISOString(),
        });
      }

      // Log error for debugging
      console.error("Error handled by useErrorHandler:", error);

      // Call custom error handler if provided
      if (onError) {
        onError(error instanceof Error ? error : new Error(String(error)));
      }

      // Show toast notification (you can integrate with your toast library here)
      if (shouldShowToast) {
        // Replace with your toast library
        console.warn("Toast notification:", errorMessage);
        // Example: toast.error(errorMessage);
      }

      return errorMessage;
    },
    [fallbackMessage, showToast, captureToSentry, onError],
  );

  return { handleError };
}
